// ---------------------------------------------------------------------------
// Renderer: Bullet List
//
// Renders a list of bullet items with two styles:
//   dot_item — colored dot + fixed label + right-aligned qualifier
//   event    — optional RAG dot + date + tag badge + headline + evidence + signals
//
// Supports multi-column flow, card-style backgrounds, compact density,
// evidence-in-tooltip, badge pills, signal squares, and overflow-aware
// item dropping with slack redistribution.
// ---------------------------------------------------------------------------

import Konva from "konva";
import CustomText from "../CustomText";
import { themeColor, defaultTheme } from "../renderers/plainObjectRenderer";
import { tagEvidence, addDebugOverlay } from "./helpers.js";

function blendHex(a, b, t) {
  const parse = h => { const m = String(h).replace("#", ""); return [parseInt(m.slice(0,2),16),parseInt(m.slice(2,4),16),parseInt(m.slice(4,6),16)]; };
  const [ar,ag,ab] = parse(a); const [br,bg,bb] = parse(b);
  const mix = (x,y) => Math.round(x + (y-x) * Math.max(0,Math.min(1,t)));
  const hx = n => n.toString(16).padStart(2,"0");
  return `#${hx(mix(ar,br))}${hx(mix(ag,bg))}${hx(mix(ab,bb))}`;
}

// Heat palette: light yellow (low) → orange (mid) → red (high)
function heatPalette(t) {
  if (t <= 0) return "#E0E0E0";
  if (t <= 0.5) return blendHex("#FFF9C4", "#FF8F00", t * 2);
  return blendHex("#FF8F00", "#C62828", (t - 0.5) * 2);
}

export function renderBulletList(block, crystalizedData, options) {
  const { x = 0, y = 0, width = 300, height: allocH, imageCallback, theme, fontScale = 1, _measureOnly } = options;
  const s = fontScale;
  const themeColors = theme || defaultTheme;
  const group = new Konva.Group({ x, y, width });
  const data = crystalizedData[block.id] || {};
  const tagStyles = block.tag_styles || {};
  const dotColorMap = block.dot_color_map || {};
  const isDotStyle = block.item_style === "dot_qualifier" || block.item_style === "dot_fact";
  const isProgressStyle = block.item_style === "progress";
  const isPreview = options._preview;

  let items = data.items || [];

  // Preview mode with no items: draw skeleton shapes directly and return.
  if (isPreview && items.length === 0) {
    const padding = Math.round(12 * s);
    const rowH = Math.round(18 * s);
    const rowGap = Math.round(8 * s);
    const barH = Math.round(8 * s);
    const SKEL = "#e8e8e8";

    if (isProgressStyle) {
      const declItems = block.items || [];
      const count = Math.min(declItems.length || 4, 6);
      let cy = Math.round(6 * s);
      for (let i = 0; i < count; i++) {
        group.add(new Konva.Rect({
          x: padding, y: cy, width: Math.round(width * 0.45), height: barH,
          fill: SKEL, cornerRadius: Math.round(2 * s)
        }));
        cy += barH + Math.round(5 * s);
        group.add(new Konva.Rect({
          x: padding, y: cy, width: width - padding * 2, height: Math.round(8 * s),
          fill: SKEL, cornerRadius: Math.round(4 * s)
        }));
        cy += Math.round(8 * s) + Math.round(12 * s);
      }
      group.height(cy + padding);
      addDebugOverlay(group, block, { ...options, height: group.height() });
      return group;
    }

    if (isDotStyle) {
      const declItems = block.items || [];
      let cy = Math.round(6 * s);
      for (const it of declItems) {
        const label = it.label?.value || it.label?.text || "•";
        group.add(new Konva.Circle({
          x: padding, y: cy + Math.round(7 * s),
          radius: Math.round(5 * s), fill: SKEL
        }));
        const labelX = padding + Math.round(14 * s);
        const labelW = Math.round(width * 0.45);
        group.add(new Konva.Rect({
          x: labelX, y: cy + Math.round(3 * s),
          width: Math.max(40, labelW - Math.round(16 * s)), height: barH,
          fill: SKEL, cornerRadius: Math.round(2 * s)
        }));
        const qualW = Math.round(width * 0.22);
        const qualX = width - padding - qualW;
        group.add(new Konva.Rect({
          x: qualX, y: cy + Math.round(3 * s),
          width: qualW, height: barH,
          fill: SKEL, cornerRadius: Math.round(2 * s)
        }));
        cy += rowH + rowGap;
      }
      group.height(cy + padding);
    } else {
      const limit = Math.min(3, block.item_limit || 3);
      let cy = Math.round(6 * s);
      for (let i = 0; i < limit; i++) {
        group.add(new Konva.Rect({
          x: padding, y: cy + Math.round(2 * s),
          width: Math.round((width - padding * 2) * (0.85 - i * 0.10)), height: barH,
          fill: SKEL, cornerRadius: Math.round(2 * s)
        }));
        cy += rowH;
        if (i < 2) {
          group.add(new Konva.Rect({
            x: padding, y: cy + Math.round(1 * s),
            width: Math.round((width - padding * 2) * (0.55 - i * 0.08)), height: Math.round(6 * s),
            fill: SKEL, cornerRadius: Math.round(2 * s)
          }));
          cy += Math.round(14 * s);
        }
      }
      group.height(cy + padding);
    }
    addDebugOverlay(group, block, { ...options, height: group.height() });
    return group;
  }

  // Multi-column flow
  const cols = Math.max(1, Math.min(block.columns || 1, Math.max(items.length, 1)));
  if (cols > 1 && !options._bulletColumn) {
    const colGap = Math.round(16 * s);
    const colW = (width - colGap * (cols - 1)) / cols;
    const per = Math.ceil(items.length / cols);
    const outer = new Konva.Group({ x, y, width });
    let outerBg = null;
    if (block.card_background) {
      outerBg = new Konva.Rect({ x: 0, y: 0, width, height: 9999, fill: block.card_background, cornerRadius: Math.round(4 * s) });
      outer.add(outerBg);
    }
    let maxH = 0;
    for (let ci = 0; ci < cols; ci++) {
      const slice = items.slice(ci * per, (ci + 1) * per);
      if (slice.length === 0) continue;
      const subData = { ...crystalizedData, [block.id]: { ...data, items: slice } };
      const colGroup = renderBulletList(block, subData, {
        ...options, x: ci * (colW + colGap), y: 0, width: colW, _bulletColumn: true
      });
      outer.add(colGroup);
      maxH = Math.max(maxH, colGroup.height() || 0);
    }
    outer.height(maxH);
    if (outerBg) outerBg.height(maxH);
    addDebugOverlay(outer, block, { ...options, height: maxH });
    return outer;
  }

  let backgroundRect = null;
  if (block.card_background && !options._bulletColumn) {
    backgroundRect = new Konva.Rect({
      x: 0, y: 0, width, height: 9999,
      fill: block.card_background, cornerRadius: Math.round(4 * s)
    });
    group.add(backgroundRect);
  }
  const padding = Math.round((block.padding ?? 12) * s);
  let cy = padding;
  const haveBudget = !_measureOnly && allocH && allocH < 99999;

  // Reserve vertical space for the heat legend so items never render into it.
  let legendReserve = 0;
  if (haveBudget && block.heat_tag) {
    const labelFs = Math.round(9 * s);
    const rowH = Math.round(labelFs * 1.6);
    const hasDesc = !!(block.heat_tag.legend_label);
    legendReserve = (hasDesc ? rowH + Math.round(3 * s) : 0) + rowH + Math.round(16 * s);
  }
  const itemAllocH = haveBudget ? allocH - legendReserve : allocH;
  const itemEntries = [];

  const compact = block.density === "compact";
  const rowGap = Number.isFinite(block.item_gap) ? Math.round(block.item_gap * s) : (compact ? Math.round(4 * s) : Math.round(8 * s));

  const smallFontSize = Math.round(10 * s);
  let maxDateWidth = 0;
  let maxTagWidth = 0;
  if (!isDotStyle) {
    if (block.heat_tag && items.some(it => it.tag)) {
      // heat_tag mode: all items use a fixed-size square — skip text measurement.
      maxTagWidth = Math.round(32 * s);
    } else {
      for (const it of items) {
        const d = it.date || "";
        if (d) {
          const p = new Konva.Text({ text: d, fontSize: smallFontSize, fontFamily: "Arial" });
          maxDateWidth = Math.max(maxDateWidth, p.width() + Math.round(12 * s));
          p.destroy();
        }
        const t = it.tag || "";
        if (t) {
          const p2 = new Konva.Text({ text: t, fontSize: smallFontSize, fontFamily: "Arial", fontStyle: "bold" });
          maxTagWidth = Math.max(maxTagWidth, p2.width() + Math.round(10 * s) + Math.round(10 * s));
          p2.destroy();
        }
      }
    }
  }

  itemLoop: for (const item of items) {
    if (haveBudget && cy >= itemAllocH) break;
    const itemStartCy = cy;
    const childCountBefore = group.getChildren().length;

    if (isProgressStyle) {
      const label = typeof item.label === 'string' ? item.label : (item.label?.value || '');
      const value = typeof item.value === 'number' ? item.value : null;
      const displayValue = item.display_value || (value != null ? `${Math.round(value)}%` : 'N/A');
      const barColor = block.bar_color || "#1F3A2E";
      const trackColor = block.track_color || "#E5E5E5";
      const valueW = Math.round(52 * s);
      const labelW = width - padding * 2 - valueW - Math.round(8 * s);

      const labelNode = new CustomText({
        x: padding, y: cy, width: labelW,
        text: label, fontSize: Math.round(11 * s), fill: "#555",
        lineHeight: 1.3, _preview: isPreview, refreshCallback: imageCallback
      });
      group.add(labelNode);

      const valueNode = new CustomText({
        x: width - padding - valueW, y: cy, width: valueW,
        text: displayValue, fontSize: Math.round(12 * s), fontStyle: "bold",
        fill: barColor, align: "right",
        lineHeight: 1.3, _preview: isPreview, refreshCallback: imageCallback
      });
      group.add(valueNode);

      const labelH = Math.max(labelNode.height(), valueNode.height());
      cy += labelH + Math.round(4 * s);

      const barW = width - padding * 2;
      const fillW = value != null ? Math.max(0, Math.min(1, value / 100)) * barW : 0;
      const barH2 = Math.round(7 * s);

      group.add(new Konva.Rect({ x: padding, y: cy, width: barW, height: barH2, fill: trackColor, cornerRadius: Math.round(3 * s) }));
      if (fillW > 0) {
        group.add(new Konva.Rect({ x: padding, y: cy, width: fillW, height: barH2, fill: barColor, cornerRadius: Math.round(3 * s) }));
      }
      cy += barH2 + Math.round(10 * s) + rowGap;
    } else if (isDotStyle) {
      const status = item.status || "";
      const dc = dotColorMap[status] || "#888";
      const label = item.label || "";
      const qualifier = item.qualifier || "";

      const contentW = width - padding * 2 - Math.round(14 * s);
      const labelW = Math.round(contentW * 0.5);
      const qualW = contentW - labelW - Math.round(12 * s);
      const labelX = padding + Math.round(14 * s);
      const qualX = labelX + labelW + Math.round(12 * s);

      group.add(new Konva.Circle({
        x: padding, y: cy + Math.round(7 * s),
        radius: Math.round(5 * s), fill: dc
      }));
      // Tag the dot with the item's provenance key
      const itemProvKey = `${block.id}.${label}`;
      group.children[group.children.length - 1].setAttr("_evidenceProvKey", itemProvKey);
      group.children[group.children.length - 1].setAttr("_blockId", block.id);

      const labelNode = new CustomText({
        x: labelX, y: cy, width: labelW,
        text: label, fontSize: Math.round(12 * s), fill: "#333",
        lineHeight: 1.3, _preview: isPreview, refreshCallback: imageCallback
      });
      labelNode.setAttr("_evidenceProvKey", itemProvKey);
      labelNode.setAttr("_blockId", block.id);
      group.add(labelNode);

      let qualHeight = 0;
      if (qualifier) {
        const qualNode = new CustomText({
          x: qualX, y: cy, width: qualW,
          text: qualifier, fontSize: Math.round(12 * s), fontStyle: "bold",
          fill: dc, lineHeight: 1.3, align: "right",
          _preview: isPreview, refreshCallback: imageCallback
        });
        group.add(qualNode);
        if (label) tagEvidence(qualNode, `${block.id}.${label}`, options);
        qualHeight = qualNode.height();
      }

      const rowH = Math.max(labelNode.height(), qualHeight, Math.round(18 * s));
      cy += rowH + Math.round(10 * s);
    } else {
      const tag = item.tag;
      const tagStyle = tagStyles[tag] || {};
      const tagBg = tagStyle.bg || "#e0e0e0";
      const tagText = tagStyle.text || "#333";
      let cx = padding;

      const headlineFontSize = Math.round((compact ? 12 : 15) * s);
      const headlineLineH = Math.round(headlineFontSize * 1.3);
      const tagBlockH = smallFontSize + Math.round(6 * s);
      const dateYOffset = Math.round((headlineLineH - smallFontSize) / 2);
      const tagYOffset = Math.round((headlineLineH - tagBlockH) / 2);

      const statusValue = item.status;
      if (statusValue && dotColorMap[statusValue]) {
        const dotRadius = Math.round(4 * s);
        const dotY = cy + Math.round(headlineLineH / 2);
        group.add(new Konva.Circle({
          x: cx + dotRadius, y: dotY,
          radius: dotRadius, fill: dotColorMap[statusValue]
        }));
        cx += dotRadius * 2 + Math.round(8 * s);
      } else if (block.dot_color) {
        const dotRadius = Math.round(4 * s);
        const dotY = cy + Math.round(headlineLineH / 2);
        group.add(new Konva.Circle({
          x: cx + dotRadius, y: dotY,
          radius: dotRadius, fill: block.dot_color
        }));
        cx += dotRadius * 2 + Math.round(8 * s);
      }

      const date = item.date || "";
      if (date) {
        const dText = new CustomText({
          x: cx, y: cy + dateYOffset,
          text: date, fontSize: smallFontSize, fill: "#888",
          lineHeight: 1, _preview: isPreview, refreshCallback: imageCallback
        });
        dText.setAttr("_evidenceProvKey", `${block.id}.${item.headline || ""}`);
        dText.setAttr("_blockId", block.id);
        group.add(dText);
        cx += maxDateWidth;
      }

      if (tag) {
        if (block.heat_tag) {
          const ht = block.heat_tag;
          const heatData = ht.source_block ? crystalizedData[ht.source_block] : null;
          const cells = heatData?.cells || [];
          const cellLabel = ht.tag_map?.[tag] ?? tag;
          const cell = cells.find(c => c.label === cellLabel);
          const v = cell ? Number(cell.value) || 0 : 0;
          const maxVal = cells.length > 0 ? Math.max(1, ...cells.map(c => Number(c.value) || 0)) : 1;
          const t = v <= 0 ? 0 : 0.15 + 0.85 * (v / maxVal);
          const cellFill = heatPalette(t);
          const cellSize = Math.round(32 * s);
          const heatCellY = cy;
          group.add(new Konva.Rect({
            x: cx, y: heatCellY, width: cellSize, height: cellSize,
            fill: cellFill, cornerRadius: Math.round(3 * s)
          }));
          if (ht.show_count !== false) {
            const numFontSize = Math.round(11 * s);
            const numColor = t >= 0.35 ? "#fff" : (v > 0 ? "#7B4A00" : "#999");
            group.add(new CustomText({
              x: cx, y: heatCellY + Math.round((cellSize - numFontSize * 1.3) / 2),
              width: cellSize, text: v > 0 ? String(v) : "–",
              fontSize: numFontSize, fontStyle: "bold", align: "center",
              fill: numColor, refreshCallback: imageCallback
            }));
          }
          cx += maxTagWidth + Math.round(10 * s);
        } else {
          const tagY = cy + tagYOffset;
          const tagLabel = new CustomText({
            x: cx, y: tagY,
            text: tag, fontSize: smallFontSize, fontStyle: "bold",
            fill: tagText, lineHeight: 1, padding: Math.round(4 * s),
            _preview: isPreview, refreshCallback: imageCallback
          });
          tagLabel.setAttr("_evidenceProvKey", `${block.id}.${item.headline || ""}`);
          tagLabel.setAttr("_blockId", block.id);
          const tagW = tagLabel.width() + Math.round(10 * s);
          const tagH = tagLabel.height() + Math.round(6 * s);
          group.add(new Konva.Rect({ x: cx, y: tagY, width: tagW, height: tagH, fill: tagBg, cornerRadius: Math.round(3 * s) }));
          tagLabel.x(cx + Math.round(5 * s));
          tagLabel.y(tagY + Math.round(3 * s));
          group.add(tagLabel);
          cx += maxTagWidth + Math.round(10 * s);
        }
      }

      let rightReserve = 0;
      const rowTopY = itemStartCy;

      const badgeVal = item.badge;
      const badgeStyles = block.badge_styles || {};
      if (badgeVal && badgeStyles[badgeVal]) {
        const bStyle = badgeStyles[badgeVal];
        const bLabel = bStyle.label || badgeVal;
        const bText = new CustomText({
          x: 0, y: 0, text: bLabel, fontSize: smallFontSize, fontStyle: "bold",
          fill: bStyle.text || "#333", _preview: isPreview, refreshCallback: imageCallback
        });
        const bW = bText.width() + Math.round(14 * s);
        const bH = smallFontSize + Math.round(7 * s);
        const bx = width - padding - bW;
        const bRect = new Konva.Rect({ x: bx, y: rowTopY, width: bW, height: bH, fill: bStyle.bg || "#e0e0e0", cornerRadius: Math.round(3 * s) });
        const itemProvKey2 = `${block.id}.${item.headline || ""}`;
        bRect.setAttr("_evidenceProvKey", itemProvKey2);
        bRect.setAttr("_blockId", block.id);
        group.add(bRect);
        bText.x(bx + Math.round(7 * s));
        bText.y(rowTopY + Math.round(3.5 * s));
        bText.setAttr("_evidenceProvKey", itemProvKey2);
        bText.setAttr("_blockId", block.id);
        group.add(bText);
        rightReserve += bW + Math.round(10 * s);
      }

      const signalColors = block.signal_colors;
      if (typeof item.signals === "string" && item.signals.trim() && signalColors) {
        const tokens = item.signals.split(",").map(t => t.trim()).filter(Boolean);
        const sq = Math.round(10 * s);
        const sqGap = Math.round(3 * s);
        const stripW = tokens.length * (sq + sqGap) - sqGap;
        let sx = width - padding - rightReserve - stripW;
        const sy = rowTopY + Math.round(2 * s);
        for (const tok of tokens) {
          const flagged = tok.endsWith("!");
          const strength = (flagged ? tok.slice(0, -1) : tok).toLowerCase();
          const sRect = new Konva.Rect({
            x: sx, y: sy, width: sq, height: sq,
            fill: signalColors[strength] || "#c9c9c9",
            cornerRadius: Math.round(1.5 * s),
            stroke: flagged ? "#6E1E2B" : undefined,
            strokeWidth: flagged ? Math.round(1.5 * s) : 0
          });
          sRect.setAttr("_evidenceProvKey", `${block.id}.${item.headline || ""}`);
          sRect.setAttr("_blockId", block.id);
          group.add(sRect);
          sx += sq + sqGap;
        }
        if (stripW > 0) rightReserve += stripW + Math.round(10 * s);
      }

      const headline = item.headline || "";
      const headlineX = cx;
      const remainingW = width - cx - padding - rightReserve;
      // If evidence is a bare URL, treat the headline as a link: blue +
      // underline, click opens the URL in a new tab. Otherwise plain text.
      const evidenceRaw = (item.evidence == null ? "" : String(item.evidence)).trim().replace(/[.,;:!?]+$/, '');
      const isUrl = /^https?:\/\/\S+$/i.test(evidenceRaw);
      if (headline && remainingW > Math.round(50 * s)) {
        const hText = new CustomText({
          x: cx, y: cy, width: remainingW,
          text: headline, fontSize: headlineFontSize,
          withMarkdown: /\*\*[^*]+\*\*|_[^_]+_/.test(headline),
          fill: isUrl ? "#1F66B0" : themeColor(themeColors, "text", "#1a1a1a"),
          textDecoration: isUrl ? "underline" : undefined,
          lineHeight: 1.3,
          _preview: isPreview, refreshCallback: imageCallback
        });
        if (isUrl) {
          hText.setAttr("url", evidenceRaw);
          hText.on("click tap", (ev) => {
            ev.cancelBubble = true;
            if (typeof window !== "undefined") window.open(evidenceRaw, "_blank", "noopener,noreferrer");
          });
          hText.on("mouseenter", () => { const st = hText.getStage(); if (st && st.container) st.container().style.cursor = "pointer"; });
          hText.on("mouseleave", () => { const st = hText.getStage(); if (st && st.container) st.container().style.cursor = "default"; });
        }
        group.add(hText);
        if (headline) tagEvidence(hText, `${block.id}.${headline}`, options);
        cy += hText.height() + Math.round(3 * s);
      }

      const evidence = block.evidence_in_tooltip ? "" : evidenceRaw;
      if (evidence && remainingW > Math.round(50 * s)) {
        const eText = new CustomText({
          x: headlineX,
          y: cy, width: width - headlineX - padding,
          text: evidence, fontSize: Math.round(12 * s),
          fill: isUrl ? "#5784B5" : themeColor(themeColors, "textSecondary", "#666"),
          lineHeight: 1.3,
          _preview: isPreview, refreshCallback: imageCallback
        });
        if (isUrl) {
          eText.setAttr("url", evidenceRaw);
          eText.on("click tap", (ev) => {
            ev.cancelBubble = true;
            if (typeof window !== "undefined") window.open(evidenceRaw, "_blank", "noopener,noreferrer");
          });
          eText.on("mouseenter", () => { const st = eText.getStage(); if (st && st.container) st.container().style.cursor = "pointer"; });
          eText.on("mouseleave", () => { const st = eText.getStage(); if (st && st.container) st.container().style.cursor = "default"; });
        }
        group.add(eText);
        cy += eText.height() + rowGap;
      } else {
        cy += rowGap;
      }
    }

    if (haveBudget && cy > itemAllocH) {
      const groupChildren = group.getChildren();
      while (groupChildren.length > childCountBefore) {
        groupChildren[groupChildren.length - 1].destroy();
      }
      cy = itemStartCy;
      break itemLoop;
    }

    itemEntries.push({
      childStart: childCountBefore,
      childEnd: group.getChildren().length
    });
  }

  if (haveBudget && itemEntries.length > 1 && !options._bulletColumn) {
    const slack = itemAllocH - cy - padding;
    if (slack > 0) {
      const extraGap = slack / (itemEntries.length - 1);
      const allChildren = group.getChildren();
      let cumOffset = 0;
      for (let i = 0; i < itemEntries.length; i++) {
        const entry = itemEntries[i];
        if (cumOffset > 0) {
          for (let c = entry.childStart; c < entry.childEnd; c++) {
            const child = allChildren[c];
            child.y(child.y() + cumOffset);
          }
        }
        if (i < itemEntries.length - 1) cumOffset += extraGap;
      }
      cy = itemAllocH - padding;
    }
  }

  // Heat legend: optional description label + gradient bar with Low / High labels.
  // When height is constrained, anchor within allocH so the clip doesn't cut it.
  if (block.heat_tag && items.some(it => it.tag)) {
    const ht = block.heat_tag;
    const barH = Math.round(7 * s);
    const labelFs = Math.round(9 * s);
    const descFs = Math.round(9 * s);
    const descLabel = ht.legend_label || "";

    // Measure all text nodes at y=0 first, then reposition after computing ly.
    const descTxt = descLabel
      ? new CustomText({ x: padding, y: 0, text: descLabel, fontSize: descFs, fill: "#555", fontStyle: "bold", refreshCallback: imageCallback })
      : null;
    const lowTxt = new CustomText({ x: padding, y: 0, text: "Low", fontSize: labelFs, fill: "#888", refreshCallback: imageCallback });
    const highTxt = new CustomText({ x: 0, y: 0, text: "High", fontSize: labelFs, fill: "#888", refreshCallback: imageCallback });

    const descH = descTxt ? descTxt.height() + Math.round(3 * s) : 0;
    const labelH = lowTxt.height();
    const legendH = descH + labelH + Math.round(4 * s);
    const ly = haveBudget ? (allocH - legendH - padding) : (cy + Math.round(10 * s));

    if (descTxt) { descTxt.y(ly); group.add(descTxt); }
    const barRowY = ly + descH;
    lowTxt.y(barRowY); highTxt.y(barRowY);
    group.add(lowTxt); group.add(highTxt);
    const barX = padding + lowTxt.width() + Math.round(5 * s);
    const barWidth = width - padding * 2 - lowTxt.width() - highTxt.width() - Math.round(10 * s);
    highTxt.x(barX + barWidth + Math.round(5 * s));
    const barY = barRowY + Math.round((labelH - barH) / 2);
    group.add(new Konva.Rect({
      x: barX, y: barY, width: barWidth, height: barH,
      fillLinearGradientStartPoint: { x: 0, y: 0 },
      fillLinearGradientEndPoint: { x: barWidth, y: 0 },
      fillLinearGradientColorStops: [0, "#FFF9C4", 0.5, "#FF8F00", 1, "#C62828"],
      cornerRadius: Math.round(2 * s)
    }));
    if (haveBudget) cy = allocH - padding;
    else cy = ly + legendH;
  }

  group.height(cy + padding);
  if (backgroundRect) backgroundRect.height(group.height());
  group.clip({ x: 0, y: 0, width, height: group.height() });
  addDebugOverlay(group, block, { ...options, height: group.height() });
  return group;
}
